"use strict";
// packages/shared-utils/src/geo/postcodeToState.ts
//
// Australian postcode → state-code lookup.
//
// Australia Post assigns postcodes in well-defined ranges per state.
// Encoding as ranges keeps the bundle tiny (zero data files, ~1KB of
// code) and lookup is O(log n) over the small range table.
//
// Sources:
//   https://en.wikipedia.org/wiki/Postcodes_in_Australia
//   https://auspost.com.au/business/marketing-and-communications/access-data-and-insights/postcode-data
//
// Plan: ~/.claude/plans/this-was-a-full-idempotent-cerf.md
//
// Edge cases handled:
//   - Leading-zero postcodes (NT and ACT use 0xxx) — accepts both
//     '0800' and '800' input.
//   - Postcodes are 4 digits zero-padded; "10" or "ten thousand" → null.
//   - Mixed cases like postcode 2618 in Yass NSW vs 2620 in ACT — the
//     range table reflects the AusPost spec; some edge ranges overlap
//     historically and we follow the canonical assignment.
Object.defineProperty(exports, "__esModule", { value: true });
exports.postcodeToState = postcodeToState;
exports.normalisePostcode = normalisePostcode;
// Ordered by `from` ascending. Multiple ranges per state are common
// (e.g. NSW splits around the ACT block at 2600-2618). Keep this list
// in numeric order — the lookup walks linearly until `from > postcode`.
//
// References (AusPost canonical):
//   ACT: 0200-0299 (PO boxes), 2600-2618, 2900-2920
//   NT:  0800-0899, 0900-0999
//   NSW: 1000-1999 (LVRs), 2000-2599, 2619-2899, 2921-2999
//   VIC: 3000-3999, 8000-8999
//   QLD: 4000-4999, 9000-9999
//   SA:  5000-5799, 5800-5999
//   WA:  6000-6797, 6800-6999
//   TAS: 7000-7799, 7800-7999
const POSTCODE_RANGES = [
    { from: 200, to: 299, state: 'ACT' },
    { from: 800, to: 899, state: 'NT' },
    { from: 900, to: 999, state: 'NT' },
    { from: 1000, to: 1999, state: 'NSW' },
    { from: 2000, to: 2599, state: 'NSW' },
    { from: 2600, to: 2618, state: 'ACT' },
    { from: 2619, to: 2899, state: 'NSW' },
    { from: 2900, to: 2920, state: 'ACT' },
    { from: 2921, to: 2999, state: 'NSW' },
    { from: 3000, to: 3999, state: 'VIC' },
    { from: 4000, to: 4999, state: 'QLD' },
    { from: 5000, to: 5999, state: 'SA' },
    { from: 6000, to: 6797, state: 'WA' },
    { from: 6800, to: 6999, state: 'WA' },
    { from: 7000, to: 7999, state: 'TAS' },
    { from: 8000, to: 8999, state: 'VIC' },
    { from: 9000, to: 9999, state: 'QLD' },
];
/**
 * Resolve an Australian postcode to its state code.
 *
 * @param raw  Postcode as captured (string or number; flexible whitespace)
 * @returns    AuStateCode if recognised, null otherwise.
 *
 * @example
 *   postcodeToState('3000')   // 'VIC'
 *   postcodeToState('800')    // 'NT'    (leading zero tolerated)
 *   postcodeToState('0800')   // 'NT'
 *   postcodeToState('xxxx')   // null
 *   postcodeToState(null)     // null
 */
function postcodeToState(raw) {
    if (raw === null || raw === undefined)
        return null;
    const trimmed = String(raw).trim();
    if (trimmed === '')
        return null;
    // Accept only digits — strip non-digit characters defensively (e.g.
    // "VIC 3000" → "3000"). If nothing's left, it's not a postcode.
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 0 || digits.length > 4)
        return null;
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 200 || n > 9999)
        return null;
    for (const r of POSTCODE_RANGES) {
        if (n >= r.from && n <= r.to)
            return r.state;
        if (n < r.from)
            break; // ranges are sorted ascending; no later range will match
    }
    return null;
}
/**
 * Normalise a free-form postcode input to the canonical 4-digit string.
 * Returns null when the input doesn't look like an AU postcode.
 *
 *   normalisePostcode('3000')       // '3000'
 *   normalisePostcode('800')        // '0800'
 *   normalisePostcode('VIC 3000')   // '3000'
 *   normalisePostcode('xxxx')       // null
 */
function normalisePostcode(raw) {
    if (raw === null || raw === undefined)
        return null;
    const digits = String(raw).trim().replace(/\D/g, '');
    if (digits.length === 0 || digits.length > 4)
        return null;
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 200 || n > 9999)
        return null;
    return String(n).padStart(4, '0');
}
